import React from 'react';

const Whitepaper: React.FC = () => {
  return (
    <div className="whitepaper-container">
      <div className="whitepaper-content">
        <h1 className="whitepaper-title">
          XPRED Token – Whitepaper
        </h1>
        
        <div className="whitepaper-subtitle">
          including Legal &amp; Compliance Framework
        </div>
        
        <div className="whitepaper-subtitle">
          Utility, Governance, and Decentralized Participation Model
        </div>

        <hr className="whitepaper-divider" />

        <section className="whitepaper-section">
          <h2 className="section-title">1. Overview</h2>
          <p className="section-text">
            XPRED is a decentralized utility and governance token used within the XPRED Prediction Platform ("the Platform"). XPRED is designed to support community-driven participation in the creation, testing, governance, and resolution of on-chain prediction markets. XPRED is <strong>not offered or marketed as an investment vehicle</strong>, and its functionality is tied directly to active participation and utility within the Platform.
          </p>
          <p className="section-text">
            The Platform remains under active development. XPRED holders play a central role in testing features, proposing enhancements, contributing ideas, and participating in the governance and dispute-resolution processes as the ecosystem evolves.
          </p>
        </section>

        <section className="whitepaper-section">
          <h2 className="section-title">2. Intended Purpose and Functionality</h2>
          <p className="section-text">
            XPRED is intended to provide access to specific functions within the Platform, including but not limited to:
          </p>
          
          <div className="subsection">
            <h3 className="subsection-title">Market Creation &amp; Governance:</h3>
            <p className="section-text">
              XPRED may be used to create new markets, propose parameters, participate in governance, and influence platform-level decisions.
            </p>
          </div>

          <div className="subsection">
            <h3 className="subsection-title">Dispute Resolution:</h3>
            <p className="section-text">
              Token holders may participate in decentralized resolution through the <strong>Top Holder Multi-Signature Council</strong>, which resolves outcomes using a 5-of-21 signing process.
            </p>
          </div>

          <div className="subsection">
            <h3 className="subsection-title">Feature Testing &amp; Community Input:</h3>
            <p className="section-text">
              XPRED holders may participate in ongoing community-driven testing and refinement of platform features.
            </p>
          </div>

          <div className="subsection">
            <h3 className="subsection-title">Claimable Platform Revenue:</h3>
            <p className="section-text">
              Holders may periodically claim their proportional share of platform income through a manual claim process inside their user dashboard. This income is generated by Platform activity and is not a passive dividend or yield.
            </p>
          </div>

          <p className="section-text">
            XPRED is intended as a <strong>participatory token</strong> for community governance, operational involvement, and platform utility.
          </p>
        </section>

        <section className="whitepaper-section">
          <h2 className="section-title">3. Decentralized Governance &amp; Multi-Signature Resolution</h2>
          <p className="section-text">
            To minimize centralization and reduce reliance on any managerial group, the Platform employs a decentralized resolution system:
          </p>
          <ul className="bullet-list">
            <li>The <strong>Top 21 XPRED holders</strong> form a governance cohort ("Resolution Council").</li>
            <li>Market outcomes are resolved using a <strong>5-of-21 native multi-signature process</strong>.</li>
            <li>No single party, entity, or development team can unilaterally resolve markets.</li>
            <li>Governance authority derives from token distribution, not appointment.</li>
          </ul>
          <p className="section-text">
            This structure is designed to avoid centralized managerial control and ensure the Platform is community-operated.
          </p>
        </section>

        <section className="whitepaper-section">
          <h2 className="section-title">4. Non-Security Considerations</h2>
          <p className="section-text">
            The XPRED token is structured with specific features intended to reduce applicability of securities laws, particularly under the U.S. Howey Test. XPRED is:
          </p>

          <div className="subsection">
            <h3 className="subsection-title">a. Not an Investment Contract</h3>
            <p className="section-text">
              XPRED is not sold or promoted as an investment, and no holder should expect profits from the efforts of a centralized team. Platform value and any claimable income depend entirely on <strong>community-driven activity</strong>, including user participation in:
            </p>
            <ul className="bullet-list">
              <li>Market creation</li>
              <li>Testing and development feedback</li>
              <li>Governance voting</li>
              <li>Outreach and platform promotion</li>
              <li>Decentralized resolution responsibilities</li>
            </ul>
          </div>

          <div className="subsection">
            <h3 className="subsection-title">b. Utility-Based Access</h3>
            <p className="section-text">
              XPRED provides functional access to tools, analytics, creation rights, governance features, and dispute resolution mechanisms.
            </p>
          </div>

          <div className="subsection">
            <h3 className="subsection-title">c. No Passive Returns</h3>
            <p className="section-text">
              XPRED does <strong>not</strong> provide automatic payouts, dividends, interest, or guaranteed financial returns.
            </p>
            <p className="section-text">
              Platform income must be <strong>manually claimed</strong>, reinforcing that XPRED is not a passive-yield instrument.
            </p>
            <p className="section-text">
              Revenue share arises from the Platform's smart-contract operations and community usage, not from managerial promises or expectations.
            </p>
          </div>

          <div className="subsection">
            <h3 className="subsection-title">d. Community-Driven Development</h3>
            <p className="section-text">
              Because the Platform is being built with community participation, no centralized group is "building value" for token holders. Token holders directly shape, test, and influence platform design.
            </p>
          </div>
        </section>

        <section className="whitepaper-section">
          <h2 className="section-title">5. Development Status &amp; Community Role</h2>
          <p className="section-text">
            Because the Platform is under active development, XPRED holders participate directly in the evolution of the system. This includes:
          </p>
          <ul className="bullet-list">
            <li>Reviewing and providing feedback on prototypes</li>
            <li>Testing new market types and resolution models</li>
            <li>Suggesting enhancements and UI/UX improvements</li>
            <li>Contributing to decentralized governance structures</li>
            <li>Helping promote and grow the platform ecosystem</li>
          </ul>
          <p className="section-text">
            Community involvement in the early and ongoing stages of development reinforces decentralized operation and reduces reliance on the core team.
          </p>
        </section>

        <section className="whitepaper-section">
          <h2 className="section-title">6. No Guarantees and Risk Acknowledgments</h2>
          <p className="section-text">
            The XPRED token and Platform carry inherent risks associated with decentralized systems and experimental technology. Token holders should understand:
          </p>
          <ul className="bullet-list">
            <li>XPRED does not guarantee financial return or appreciation.</li>
            <li>Platform income amounts may vary and are dependent on user activity.</li>
            <li>The continued development and operation of the Platform relies on community contribution and participation.</li>
            <li>Regulatory treatment of digital assets continues to evolve, and no assurances can be made regarding future classifications.</li>
          </ul>
        </section>

        <section className="whitepaper-section">
          <h2 className="section-title">7. Transparency &amp; Governance Evolution</h2>
          <p className="section-text">
            To support long-term compliance and decentralization:
          </p>
          <ul className="bullet-list">
            <li>Governance mechanisms may be expanded or revised through community proposals.</li>
            <li>Smart contracts may be open-sourced or made publicly auditable.</li>
            <li>Additional transparency tools may be introduced, including governance dashboards, proposal trackers, and dispute-resolution logs.</li>
            <li>The Platform may transition toward increased decentralization over time, depending on community participation levels.</li>
          </ul>
        </section>

        <section className="whitepaper-section">
          <h2 className="section-title">8. Summary</h2>
          <p className="section-text">
            XPRED is structured as a <strong>utility-based, governance-focused, community-operated token</strong> underpinning a decentralized prediction-market platform. It is intentionally designed <strong>not to function as a passive investment security</strong>, and its value and utility depend on <strong>active community participation, governance, and contribution</strong>.
          </p>
          <p className="section-text">
            XPRED enables users to help build, test, govern, and operate the Platform, aligning control and responsibility with the token-holding community.
          </p>
        </section>

        <section className="whitepaper-section">
          <h2 className="section-title">9. Token Distribution &amp; Supply Structure</h2>
          
          <div className="subsection">
            <h3 className="subsection-title">Total Supply</h3>
            <p className="section-text">
              The XPRED token has a <strong>fixed maximum supply of 1,000,000 tokens</strong>. No additional tokens can be minted beyond this cap. This finite supply supports predictable governance weighting and transparent participation across the Platform.
            </p>
          </div>

          <div className="subsection">
            <h3 className="subsection-title">Initial Allocation</h3>
            <p className="section-text">
              XPRED's initial distribution is designed to ensure broad community access, transparent governance, and adequate resources for ongoing development and maintenance of the Platform.
            </p>
          </div>

          <div className="subsection">
            <h3 className="subsection-title">a. Community Allocation – 800,000 XPRED (80%)</h3>
            <p className="section-text">
              <strong>800,000 XPRED</strong> will be made available to community participants.
            </p>
            <p className="section-text">
              Tokens may be acquired at an initial sales price of <strong>0.25 USDTEST per XPRED</strong>.
            </p>
            <p className="section-text">
              This initial sale price establishes a <strong>starting platform valuation of $250,000</strong>, based solely on the initial token pricing and circulating supply.
            </p>
            <p className="section-text">
              The purpose of this allocation is to provide widespread access to the XPRED governance ecosystem and to distribute operational influence across a broad base of participants.
            </p>
          </div>

          <div className="subsection">
            <h3 className="subsection-title">b. Developer Allocation (Homebloks Inc.) – 200,000 XPRED (20%)</h3>
            <p className="section-text">
              <strong>200,000 XPRED</strong> is allocated to <strong>Homebloks Inc.</strong>, the initial developer of the Platform.
            </p>
            <p className="section-text">
              Developer-held XPRED tokens participate in governance to the same extent as all other tokens and do not convey any special rights, guarantees, or preferential treatment.
            </p>
          </div>

          <div className="subsection">
            <h3 className="subsection-title">Transferability</h3>
            <p className="section-text">
              Once distributed, XPRED tokens are <strong>freely transferable</strong>.
            </p>
            <p className="section-text">
              Holders may buy, sell, or transfer tokens at their discretion using supported interfaces or decentralized marketplaces.
            </p>
            <p className="section-text">
              Token transfers do not guarantee or imply any form of financial return, appreciation, or investment outcome.
            </p>
          </div>

          <div className="subsection">
            <h3 className="subsection-title">No Investment Representation</h3>
            <p className="section-text">
              The distribution of XPRED tokens—whether to community participants or to the developer—<strong>does not constitute an offer of securities</strong>, debt instruments, or investment contracts.
            </p>
            <p className="section-text">
              XPRED is provided exclusively for its intended purposes within the Platform, including:
            </p>
            <ul className="bullet-list">
              <li>Access to governance tools</li>
              <li>Market creation functions</li>
              <li>Dispute-resolution participation</li>
              <li>Feature testing</li>
              <li>Claim-based access to platform income generated through community activity</li>
            </ul>
            <p className="section-text">
              The token distribution should not be interpreted as offering future profit, guaranteed value, or passive financial benefits.
            </p>
          </div>

          <div className="subsection">
            <h3 className="subsection-title">Decentralization Considerations</h3>
            <p className="section-text">
              The distribution structure, with 80% of the supply available to the community, is intended to foster:
            </p>
            <ul className="bullet-list">
              <li>Broad governance participation</li>
              <li>Reduced centralization risk</li>
              <li>Diverse representation in the multi-signature resolution cohort</li>
              <li>Widespread community involvement in platform operations</li>
            </ul>
            <p className="section-text">
              The developer's allocation is capped at 20% and does not allow unilateral control over governance or platform decisions.
            </p>
          </div>

          <div className="subsection">
            <h3 className="subsection-title">Summary</h3>
            <p className="section-text">
              The XPRED token supply is fixed, transparent, and purpose-built to support a decentralized prediction-market ecosystem. The distribution model ensures that governance authority rests primarily with the community, while providing <strong>Homebloks Inc.</strong> with sufficient resources to continue supporting the Platform's technical and operational needs without granting disproportionate influence or investment-like rights.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Whitepaper;
